import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, signal } from '@angular/core';
import { MusicDataService } from '../music-data.service';
import { DeezerLinkComponent } from '../shared/deezer-link.component';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatCard, MatCardContent, MatCardHeader } from '@angular/material/card';
import { MatIconButton } from '@angular/material/button';
import { FlexModule } from '@ngbracket/ngx-layout/flex';
import { ExtendedModule } from '@ngbracket/ngx-layout/extended';
import { RouterLink } from '@angular/router';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-playlist',
  templateUrl: './playlist.component.html',
  styleUrls: ['./playlist.component.css'],
  imports: [CommonModule, MatIcon, MatCard, MatCardContent, MatCardHeader, MatIconButton, FlexModule, ExtendedModule, RouterLink, DeezerLinkComponent]
})
export class PlaylistComponent implements OnInit, OnDestroy {
  public tracks = signal<any[]>([]);
  public currentTrackIndex = signal(0);
  public isPlaying = signal(false);
  public currentAudio: HTMLAudioElement | null = null;
  public currentTime = signal(0);
  public duration = signal(0);
  public volume = signal(1);
  public showVolumeSlider = signal(false);

  private favSub: Subscription | null = null;
  private removeSub: Subscription | null = null;

  protected Math = Math;
  private pipWindow: Window | null = null;

  constructor(private data: MusicDataService) { }

  ngOnInit(): void {
    this.loadPlaylist();
    this.initPopoutListener();
  }

  loadPlaylist(): void {
    this.favSub = this.data.getFavourites(1, 100).subscribe({
      next: (data) => {
        if (data && data.tracks) {
          this.tracks.set(data.tracks);
          console.log('Playlist loaded with', data.tracks.length, 'tracks');
        }
      },
      error: (error) => {
        console.error('Error loading playlist:', error);
      }
    });
  }

  playTrack(index: number): void {
    const track = this.tracks()[index];
    if (!track) return;

    // Stop current audio
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.onended = null;
    }

    this.currentTrackIndex.set(index);

    if (!track.preview_url) {
      console.warn('No preview available for track:', track.name);
      this.isPlaying.set(false);
      return;
    }

    this.currentAudio = new Audio(track.preview_url);
    this.currentAudio.volume = this.volume();
    this.currentAudio.onended = () => {
      this.nextTrack();
    };

    // Update time display as track plays
    this.currentAudio.ontimeupdate = () => {
      this.currentTime.set(this.currentAudio?.currentTime || 0);
      this.duration.set(this.currentAudio?.duration || 0);
    };

    this.currentAudio.play();
    this.isPlaying.set(true);
  }

  pauseTrack(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.isPlaying.set(false);
    }
  }

  resumeTrack(): void {
    if (this.currentAudio && !this.isPlaying()) {
      this.currentAudio.play();
      this.isPlaying.set(true);
    }
  }

  togglePlayPause(): void {
    if (this.isPlaying()) {
      this.pauseTrack();
    } else if (this.currentAudio) {
      this.resumeTrack();
    } else {
      this.playTrack(this.currentTrackIndex());
    }
  }

  nextTrack(): void {
    const nextIndex = this.currentTrackIndex() + 1;
    if (nextIndex < this.tracks().length) {
      this.playTrack(nextIndex);
    } else {
      this.pauseTrack();
      this.currentTrackIndex.set(this.tracks().length - 1);
    }
  }

  previousTrack(): void {
    const prevIndex = this.currentTrackIndex() - 1;
    if (prevIndex >= 0) {
      this.playTrack(prevIndex);
    }
  }

  skipToTrack(index: number): void {
    this.playTrack(index);
  }

  setVolume(value: number): void {
    value = Number(value);
    this.volume.set(value);
    if (this.currentAudio) {
      this.currentAudio.volume = value;
    }
  }

  toggleVolumeSlider(): void {
    this.showVolumeSlider.update(v => !v);
  }

  popoutPlayerWindow: Window | null = null;

  popoutPlayer(): void {
    // Close existing window if open
    if (this.popoutPlayerWindow && !this.popoutPlayerWindow.closed) {
      this.popoutPlayerWindow.close();
    }

    // Small compact player window
    const width = 320;
    const height = 400;

    // Position at top-right of screen
    const screenX = window.screen.width - width - 20;
    const screenY = 20;

    // Open new window
    this.popoutPlayerWindow = window.open(
      '',
      'popoutPlayer',
      `width=${width},height=${height},left=${screenX},top=${screenY},resizable=yes,scrollbars=no`
    );

    if (!this.popoutPlayerWindow) {
      alert('Please allow popups for this site to use the pop-out player');
      return;
    }

    // Build the player HTML
    const playerHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Now Playing</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            height: 100vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            overflow: hidden;
          }
          .album-art {
            width: 200px;
            height: 200px;
            border-radius: 8px;
            box-shadow: 0 8px 30px rgba(0,0,0,0.3);
            margin-bottom: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
          }
          .album-art img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          .album-art-placeholder {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          }
          .track-info {
            text-align: center;
            color: white;
            padding: 0 16px;
            margin-bottom: 16px;
          }
          .track-name {
            font-size: 16px;
            font-weight: 600;
            margin-bottom: 4px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 280px;
          }
          .artist-name {
            font-size: 14px;
            opacity: 0.9;
          }
          .controls {
            display: flex;
            gap: 8px;
            align-items: center;
          }
          button {
            background: rgba(255,255,255,0.2);
            border: none;
            border-radius: 50%;
            width: 44px;
            height: 44px;
            cursor: pointer;
            color: white;
            font-size: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: background 0.2s;
          }
          button:hover { background: rgba(255,255,255,0.35); }
          button:disabled { opacity: 0.4; cursor: not-allowed; }
          button.play-btn {
            width: 56px;
            height: 56px;
            font-size: 28px;
          }
          .volume-control {
            display: flex;
            align-items: center;
            gap: 4px;
          }
          .volume-slider {
            width: 60px;
            accent-color: white;
          }
          .progress {
            width: 100%;
            padding: 0 16px;
            margin-top: 12px;
          }
          .progress-bar {
            height: 4px;
            background: rgba(255,255,255,0.3);
            border-radius: 2px;
            overflow: hidden;
          }
          .progress-fill {
            height: 100%;
            background: white;
            transition: width 0.1s linear;
          }
          .time-display {
            display: flex;
            justify-content: space-between;
            font-size: 11px;
            margin-top: 4px;
            opacity: 0.8;
          }
        </style>
      </head>
      <body>
        <div class="album-art" id="albumArt"><img id="albumImg" src="" alt="Album Art" style="display:none;" /></div>
        <div class="track-info">
          <div class="track-name" id="trackName">No track selected</div>
          <div class="artist-name" id="artistName"></div>
        </div>
        <div class="progress">
          <div class="progress-bar">
            <div class="progress-fill" id="progressFill" style="width: 0%"></div>
          </div>
          <div class="time-display">
            <span id="currentTime">0:00</span>
            <span id="duration">0:00</span>
          </div>
        </div>
        <br />
        <div class="controls">
          <button id="prevBtn" title="Previous">⏮</button>
          <button class="play-btn" id="playBtn" title="Play/Pause">▶</button>
          <button id="nextBtn" title="Next">⏭</button>
          <div class="volume-control">
            <button id="volBtn" title="Volume" style="width:36px;height:36px;font-size:16px">🔊</button>
            <input type="range" min="0" max="1" step="0.01" value="1" class="volume-slider" id="volumeSlider" />
          </div>
        </div>
        <script>
          let isPlaying = false;
          let volume = 1;
          let albumArtUrl = '';

          // Listen for updates from parent window
          window.addEventListener('message', (e) => {
            if (e.data.type === 'update') {
              const data = e.data;
              console.log('Popup received update:', data);
              document.getElementById('trackName').textContent = data.trackName || 'No track selected';
              document.getElementById('artistName').textContent = data.artistName || '';

              albumArtUrl = data.albumArtUrl || '';
              const imgEl = document.getElementById('albumImg');
              const albumArtEl = document.getElementById('albumArt');
              if (albumArtUrl) {
                imgEl.src = albumArtUrl;
                imgEl.style.display = 'block';
                albumArtEl.style.background = 'none';
              } else {
                imgEl.src = '';
                imgEl.style.display = 'none';
                albumArtEl.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
              }

              document.getElementById('progressFill').style.width = (data.progress || 0) + '%';
              document.getElementById('currentTime').textContent = data.currentTime || '0:00';
              document.getElementById('duration').textContent = data.duration || '0:00';
              isPlaying = data.isPlaying || false;
              document.getElementById('playBtn').textContent = isPlaying ? '⏸' : '▶';
              volume = data.volume !== undefined ? data.volume : volume;
              document.getElementById('volumeSlider').value = volume;
              updateVolIcon();
            }
          });

          function updateVolIcon() {
            const volBtn = document.getElementById('volBtn');
            if (volume === 0) volBtn.textContent = '🔇';
            else if (volume < 0.5) volBtn.textContent = '🔉';
            else volBtn.textContent = '🔊';
          }

          // Control handlers
          document.getElementById('prevBtn').addEventListener('click', function() {
            window.opener.postMessage({ action: 'previous' }, '*');
          });

          document.getElementById('playBtn').addEventListener('click', function() {
            window.opener.postMessage({ action: 'togglePlay' }, '*');
          });

          document.getElementById('nextBtn').addEventListener('click', function() {
            window.opener.postMessage({ action: 'next' }, '*');
          });

          document.getElementById('volumeSlider').addEventListener('input', function(e) {
            volume = e.target.value;
            window.opener.postMessage({ action: 'setVolume', value: volume }, '*');
            updateVolIcon();
          });
        </script>
      </body>
      </html>
    `;

    this.popoutPlayerWindow.document.write(playerHtml);
    this.popoutPlayerWindow.document.close();

    // Start sending updates to the popout window
    this.sendPopoutUpdates();
  }

  private sendPopoutUpdates(): void {
    if (!this.popoutPlayerWindow || this.popoutPlayerWindow.closed) {
      return;
    }

    const track = this.tracks()[this.currentTrackIndex()];
    const albumArtUrl = track?.album?.images?.[0]?.url || null;

    this.popoutPlayerWindow.postMessage({
      type: 'update',
      trackName: track?.name || '',
      artistName: track?.artists?.map((a: any) => a.name).join(', ') || '',
      albumArtUrl,
      progress: this.duration() ? (this.currentTime() / this.duration()) * 100 : 0,
      currentTime: this.formatTime(this.currentTime()),
      duration: this.formatTime(this.duration()),
      isPlaying: this.isPlaying(),
      volume: this.volume()
    }, '*');

    // Continue updates
    setTimeout(() => this.sendPopoutUpdates(), 500);
  }

  private initPopoutListener(): void {
    window.addEventListener('message', (event) => {
      // Only accept messages from our popout window
      if (this.popoutPlayerWindow && event.source !== this.popoutPlayerWindow) {
        return;
      }

      const { action, value } = event.data || {};
      if (!action) return;

      console.log('Popout message received:', action, value);

      switch (action) {
        case 'togglePlay':
          this.togglePlayPause();
          break;
        case 'previous':
          this.previousTrack();
          break;
        case 'next':
          this.nextTrack();
          break;
        case 'setVolume':
          this.setVolume(parseFloat(value));
          break;
      }
    });
  }

  async togglePictureInPicture(): Promise<void> {
    // Use popout player instead of PiP
    this.popoutPlayer();
  }

  removeFromPlaylist(trackId: string): void {
    this.removeSub = this.data.removeFromFavourites(trackId).subscribe({
      next: () => {
        // Reload playlist after removal
        this.loadPlaylist();
      },
      error: (error) => {
        console.error('Error removing track:', error);
      }
    });
  }

  formatTime(seconds: number): string {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  ngOnDestroy(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.onended = null;
    }
    if (this.popoutPlayerWindow && !this.popoutPlayerWindow.closed) {
      this.popoutPlayerWindow.close();
    }
    this.favSub?.unsubscribe();
    this.removeSub?.unsubscribe();
  }
}
